package se.mittpass.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.WorkplaceDto
import se.mittpass.web.rememberWindowWidth

@Composable
fun DashboardBreadcrumbs(
    workplaces: List<WorkplaceDto>,
    selectedWorkplaceId: Long?,
    onSelectWorkplace: (Long) -> Unit,
    currentTabLabel: String,
    onTabClick: () -> Unit,
    contextLabel: String? = null,
    onContextClick: (() -> Unit)? = null,
    isMobile: Boolean = false
) {
    val windowWidth = rememberWindowWidth()
    if (isMobile || windowWidth < 1024) return
    val isCompactDesktop = windowWidth < 1480

    Div({
        classes("dashboard-breadcrumbs-container")
        style {
            property("display", "flex")
            property("align-items", "center")
            property("gap", if (isCompactDesktop) "0.25rem" else "0.4rem")
            property("font-size", if (isCompactDesktop) "0.78rem" else "0.85rem")
            property("white-space", "nowrap")
            property("min-width", "0")
            property("flex-shrink", "1")
            property("overflow", "hidden")
        }
    }) {
        // Vertical divider after brand
        Div({
            style {
                property("width", "1px")
                property("height", "18px")
                property("background", "var(--rp-hl-med)")
                property("margin", if (isCompactDesktop) "0 0.15rem" else "0 0.3rem")
                property("flex-shrink", "0")
            }
        }) {}

        // Tier 1: Workplace dropdown
        if (workplaces.isNotEmpty()) {
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.3rem")
                    property("padding", if (isCompactDesktop) "0.2rem 0.45rem" else "0.25rem 0.6rem")
                    property("border-radius", "8px")
                    property("background", "var(--rp-surface)")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("min-width", "0")
                    property("flex-shrink", "1")
                }
            }) {
                FaIcon("fa-solid fa-store", size = "0.75rem", color = "var(--rp-pine)")
                Select({
                    attr("value", selectedWorkplaceId?.toString() ?: "")
                    onChange { e -> (e.target.asDynamic().value as String?)?.toLongOrNull()?.let(onSelectWorkplace) }
                    style {
                        property("border", "none")
                        property("outline", "none")
                        property("background", "transparent")
                        property("font-size", if (isCompactDesktop) "0.78rem" else "0.84rem")
                        property("font-weight", "700")
                        property("color", "var(--rp-text)")
                        property("cursor", "pointer")
                        property("font-family", "'Manrope', system-ui, sans-serif")
                        property("max-width", if (isCompactDesktop) "105px" else "150px")
                        property("text-overflow", "ellipsis")
                    }
                }) {
                    workplaces.forEach { w -> Option(w.id.toString()) { Text(w.name) } }
                }
            }
        }

        // Separator 1 & Tier 2 Active Tab (shown at >= 1200px; at 1024-1199px active section is already highlighted in center nav)
        if (windowWidth >= 1200) {
            Span({
                style {
                    property("color", "var(--rp-muted)")
                    property("font-weight", "500")
                    property("padding", "0 0.05rem")
                    property("user-select", "none")
                    property("flex-shrink", "0")
                }
            }) { Text("/") }

            Button({
                attr("type", "button")
                onClick { onTabClick() }
                style {
                    property("background", "transparent")
                    property("border", "none")
                    property("padding", if (isCompactDesktop) "0.2rem 0.35rem" else "0.25rem 0.45rem")
                    property("border-radius", "6px")
                    property("font-size", if (isCompactDesktop) "0.78rem" else "0.84rem")
                    property("font-weight", if (contextLabel == null || isCompactDesktop) "700" else "600")
                    property("color", if (contextLabel == null || isCompactDesktop) "var(--rp-text)" else "var(--rp-subtle)")
                    property("cursor", "pointer")
                    property("font-family", "inherit")
                    property("white-space", "nowrap")
                    property("flex-shrink", "0")
                    property("transition", "color 0.15s ease")
                }
            }) {
                Text(currentTabLabel)
            }
        }

        // Tier 3: Optional context pill (shown when there is ample horizontal width >= 1480px)
        if (!contextLabel.isNullOrBlank() && !isCompactDesktop) {
            Span({
                style {
                    property("color", "var(--rp-muted)")
                    property("font-weight", "500")
                    property("padding", "0 0.05rem")
                    property("user-select", "none")
                    property("flex-shrink", "0")
                }
            }) { Text("/") }

            Span({
                onClick { onContextClick?.invoke() }
                style {
                    property("background", "var(--rp-info-bg)")
                    property("color", "var(--rp-pine)")
                    property("padding", "0.18rem 0.5rem")
                    property("border-radius", "50px")
                    property("font-size", "0.75rem")
                    property("font-weight", "700")
                    property("white-space", "nowrap")
                    property("flex-shrink", "0")
                    if (onContextClick != null) property("cursor", "pointer")
                }
            }) {
                Text(contextLabel)
            }
        }
    }
}
